import React, { useEffect, useState, useCallback } from 'react';
import { Bell, CheckCircle, Calendar, Phone, Clock, AlertTriangle, Users, BarChart3, Check, CheckCheck } from 'lucide-react';
import { apiService, Notification } from '../services/api';
import socketService from '../services/socket';

const typeIcons: Record<Notification['type'], React.ReactNode> = {
    meeting_reminder: <Calendar size={18} />,
    call_alert: <Phone size={18} />,
    follow_up_warning: <Clock size={18} />,
    daily_status: <BarChart3 size={18} />,
    ignored_lead: <Users size={18} />,
    system_error: <AlertTriangle size={18} />,
};

const priorityColors: Record<Notification['priority'], string> = {
    low: 'var(--blue)',
    medium: 'var(--warning)',
    high: 'var(--orange)',
    critical: 'var(--error)',
};

export const NotificationsPage: React.FC = () => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<{ type?: string; unreadOnly: boolean }>({ unreadOnly: false });

    const loadNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const result = await apiService.getNotifications({
                type: filter.type,
                unreadOnly: filter.unreadOnly,
                limit: 50,
            });
            setNotifications(result.notifications);
            setTotal(result.total);
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        loadNotifications();

        const socket = socketService.connect();
        socket.on('new_notification', (notification: Notification) => {
            setNotifications(prev => [notification, ...prev]);
            setTotal(prev => prev + 1);
        });

        socket.on('notification_read', ({ id }: { id: string }) => {
            setNotifications(prev =>
                prev.map(n => n._id === id ? { ...n, dashboardRead: true } : n)
            );
        });

        socket.on('all_notifications_read', () => {
            setNotifications(prev => prev.map(n => ({ ...n, dashboardRead: true })));
        });

        return () => {
            socket.off('new_notification');
            socket.off('notification_read');
            socket.off('all_notifications_read');
        };
    }, [loadNotifications]);

    const handleMarkAsRead = async (id: string) => {
        try {
            await apiService.markNotificationAsRead(id);
            setNotifications(prev =>
                prev.map(n => n._id === id ? { ...n, dashboardRead: true } : n)
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await apiService.markAllNotificationsAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, dashboardRead: true })));
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    };

    const unreadCount = notifications.filter(n => !n.dashboardRead).length;

    return (
        <div className="page-grid">
            <section className="panel span-3">
                <header className="panel-header">
                    <div>
                        <p className="eyebrow">Notifications</p>
                        <h2>
                            <Bell size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                            All Notifications
                        </h2>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <span className="soft-chip">{total} total</span>
                        {unreadCount > 0 && (
                            <span className="pill highlight">{unreadCount} unread</span>
                        )}
                        <button className="ghost-button" onClick={handleMarkAllAsRead} disabled={unreadCount === 0}>
                            <CheckCheck size={16} />
                            Mark all read
                        </button>
                    </div>
                </header>

                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                    <button
                        className={`pill ${!filter.type ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, type: undefined })}
                    >
                        All
                    </button>
                    <button
                        className={`pill ${filter.type === 'meeting_reminder' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, type: 'meeting_reminder' })}
                    >
                        Meetings
                    </button>
                    <button
                        className={`pill ${filter.type === 'follow_up_warning' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, type: 'follow_up_warning' })}
                    >
                        Follow-ups
                    </button>
                    <button
                        className={`pill ${filter.type === 'ignored_lead' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, type: 'ignored_lead' })}
                    >
                        Ignored Leads
                    </button>
                    <button
                        className={`pill ${filter.type === 'system_error' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, type: 'system_error' })}
                    >
                        Errors
                    </button>
                    <div style={{ marginLeft: 'auto' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={filter.unreadOnly}
                                onChange={(e) => setFilter({ ...filter, unreadOnly: e.target.checked })}
                            />
                            Unread only
                        </label>
                    </div>
                </div>

                {loading ? (
                    <div className="loading-panel">
                        <div className="spinner"></div>
                        <p>Loading notifications...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="empty-state">
                        <Bell size={48} />
                        <p>No notifications found.</p>
                    </div>
                ) : (
                    <div className="notification-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {notifications.map((notification) => (
                            <article
                                key={notification._id}
                                className="notification-card"
                                style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    padding: '1rem',
                                    background: notification.dashboardRead ? 'var(--surface-muted)' : 'var(--surface)',
                                    borderRadius: 'var(--radius-md)',
                                    borderLeft: `4px solid ${priorityColors[notification.priority]}`,
                                    opacity: notification.dashboardRead ? 0.7 : 1,
                                }}
                            >
                                <div style={{ color: priorityColors[notification.priority] }}>
                                    {typeIcons[notification.type]}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <h3 style={{ margin: 0, fontSize: '1rem' }}>{notification.title}</h3>
                                        <time style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                            {formatDate(notification.sentAt)}
                                        </time>
                                    </div>
                                    <p style={{ margin: '0.5rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                                        {notification.message}
                                    </p>
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                                        <span className={`pill ${notification.priority === 'critical' ? 'error' : notification.priority === 'high' ? 'warning' : 'muted'}`}>
                                            {notification.priority}
                                        </span>
                                        {notification.whatsappDelivered && (
                                            <span className="pill success" style={{ fontSize: '0.7rem' }}>
                                                <CheckCircle size={12} /> WhatsApp sent
                                            </span>
                                        )}
                                        {notification.customerId && (
                                            <span className="pill muted" style={{ fontSize: '0.7rem' }}>
                                                {notification.customerId.name || notification.customerId.whatsappName || 'Customer'}
                                            </span>
                                        )}
                                        {!notification.dashboardRead && (
                                            <button
                                                className="ghost-button"
                                                style={{ marginLeft: 'auto', padding: '0.25rem 0.5rem' }}
                                                onClick={() => handleMarkAsRead(notification._id)}
                                            >
                                                <Check size={14} /> Mark read
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};
